import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}!`);
      navigate(res.user.role === "admin" ? "/admin" : "/");
    } else {
      setError(res.error);
    }
  };

  return (
    <section className="section-pad">
      <div className="container-xl flex justify-center">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="login-card">
          <div className="mb-6 text-center">
            <img src="/ehp-logo.jpg" alt="EHP" className="mx-auto h-14 w-14 rounded-full bg-white object-contain ring-1 ring-border" />
            <h1 className="mt-4 font-serif text-3xl">Welcome back</h1>
            <p className="mt-2 text-sm text-muted-foreground">Sign in to your EHP account</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <input required type="email" placeholder="Email" className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
            <div className="relative">
              <input
                required
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="input-field pr-11"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="login-submit-button">
              {loading ? "Signing in..." : (<>Sign In <LogIn className="h-4 w-4" /></>)}
            </button>
          </form>
          <GoogleSignInButton testId="login-google-button" />
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account? <Link to="/register" className="font-600 text-primary hover:underline" data-testid="login-to-register">Create one</Link>
          </p>
        </div>
      </div>
    </section>
  );
}
